/**
 * Gift Card Service
 * API functions for gift card operations
 */

import axios from 'axios';
import { getUID } from '@/utils';

const API_BASE_URL = 'https://api.jackopot.com';

/**
 * Search gift card by unique key
 * @param {string} uniqueKey - Gift card unique key
 * @returns {Promise}
 */
export const searchGiftCard = async (uniqueKey) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/giftCard/search`, {
      uniqueKey: uniqueKey.trim()
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching gift card:', error);
    throw error;
  }
};

/**
 * Activate gift card by unique key
 * @param {string} uniqueKey - Gift card unique key
 * @returns {Promise}
 */
export const activateGiftCard = async (uniqueKey) => {
  try {
    const userId = getUID();
    
    if (!userId) {
      throw new Error('User ID not found. Please login first.');
    }

    const response = await axios.post(`${API_BASE_URL}/giftCard/activate`, {
      userId: userId,
      uniqueKey: uniqueKey
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error activating gift card:', error);
    throw error;
  }
};

/**
 * Claim gift card
 * @param {string|number} giftCardId - Gift card ID
 * @returns {Promise}
 */
export const claimGiftCard = async (giftCardId) => {
  try {
    const userId = getUID();
    
    if (!userId) {
      throw new Error('User ID not found. Please login first.');
    }

    if (!giftCardId) {
      throw new Error('Gift card ID is required');
    }

    const response = await axios.post(`${API_BASE_URL}/giftCard/claim`, {
      userId: userId,
      giftCardId: giftCardId
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error claiming gift card:', error);
    throw error;
  }
};

/**
 * Fetch user's active gift cards with status
 * @param {string|number} userId - User ID
 * @returns {Promise}
 */
export const fetchUserGiftCards = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const response = await axios.get(`${API_BASE_URL}/giftCard/user/${userId}/active-with-status`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user gift cards:', error);
    throw error;
  }
};
